import React, { type FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import { useUserStore } from "@app/store/useUserStore";
import confirm from "@app/services/confirm/confirm";
import { CardHeader } from "@shared/ui/card-header";
import { CardLoader } from "@src/components/card-loader";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import {
    BusinessDivisionValue,
    BusinessMeasureValue,
    BusinessStockObjValue,
    BusinessUserValue,
} from "@src/components/business-data-display";
import { DetailList } from "@shared/ui/detail-row";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { formatEmptyDisplayValue } from "@shared/utils/formatEmptyDisplayValue";
import { stockKanbanContainersServices } from "../../../../api/stockKanbanContainers";
import { formatComplectState } from "../../helpers/formatComplectState";
import { formatRemainderCells } from "../../helpers/formatRemainderCells";
import { buildStockKanbanSupplyDivisionListUrl } from "../SearchList/stockKanbanSupplyListUrl";
import {
    StockKanbanSupplyContentsList,
    sumKanbanContentQty,
} from "./components/StockKanbanSupplyContentsList/StockKanbanSupplyContentsList";
import { StockKanbanSupplyScanPlaceholder } from "./components/StockKanbanSupplyScanPlaceholder/StockKanbanSupplyScanPlaceholder";
import { useStockKanbanSupplyCardQuery, useStockKanbanSupplyContentsQuery } from "./hooks";
import { useStockKanbanSupplyCardMutations } from "./useStockKanbanSupplyCardMutations";
import { stockKanbanSupplyCardWindows } from "./stockKanbanSupplyCard.windows";
import "./StockKanbanSupplyCard.scss";

const STOCK_KANBAN_SUPPLY_CARD_SCAN_PAUSE_WINDOW_KEYS = [stockKanbanSupplyCardWindows.content.key];

/** Карточка контейнера КАНБАН для комплектования партий. */
export const StockKanbanSupplyCard: FC = () => {
    const containerCode = useRequiredNumberParam("code", "код контейнера КАНБАН");
    const navigate = useNavigate();
    const windows = useWindows();
    const actionSheet = useActionSheet();
    const currentUserCode = useUserStore((state) => state.currentUser?.Code ?? null);

    const cardQuery = useStockKanbanSupplyCardQuery(containerCode);
    const contentsQuery = useStockKanbanSupplyContentsQuery(containerCode);
    const container = cardQuery.data;
    const collectedQty = sumKanbanContentQty(contentsQuery.content);
    const isFullyCollected = container != null && collectedQty >= container.Qty;
    const canTake = container != null ? stockKanbanContainersServices.canTakeForComplect(container) : false;
    const canFinish =
        container != null ? stockKanbanContainersServices.canFinishKanbanComplect(container, currentUserCode) : false;
    const canManageContents =
        container != null ? stockKanbanContainersServices.canManageKanbanContents(container, currentUserCode) : false;
    const readonly = canManageContents === false;

    const isCardScannerSuppressedByWindow = useIsOverlayOpenByKeys(STOCK_KANBAN_SUPPLY_CARD_SCAN_PAUSE_WINDOW_KEYS);

    const mutations = useStockKanbanSupplyCardMutations({
        onContentsReplace: (contents) => {
            contentsQuery.replaceContents(contents);
        },
        onContentRemove: (contentCode) => {
            contentsQuery.removeContent(contentCode);
        },
        onContainerTaken: (nextContainer) => {
            cardQuery.setData(nextContainer);
        },
        onComplete: () => {
            const divisionCode = cardQuery.data?.DivisionCode;
            /*
             * После завершения уводим на список склада завершённого контейнера
             * со всеми статусами и без отбора по комплектовщику: новый фильтр в URL
             * = новый query key, список загрузится свежим с сервера.
             */
            if (divisionCode != null && divisionCode !== 0) {
                navigate(buildStockKanbanSupplyDivisionListUrl(divisionCode));
                return;
            }
            navigate("..", { relative: "path" });
        },
    });

    const openContentWindow = useCallback(
        (contentCode?: number, initialLotCode?: number | null, initialQty?: number, initialSpCode?: number | null): void => {
            if (container == null) {
                return;
            }

            const isCreate = contentCode == null;
            if (isCreate && readonly) {
                return;
            }

            stockKanbanSupplyCardWindows.content.open(windows, {
                containerCode,
                container,
                contentCode,
                initialLotCode,
                initialQty,
                initialSpCode,
                readonly,
                createContentMutation: mutations.createContentMutation,
                updateContentMutation: mutations.updateContentMutation,
                removeContentMutation: mutations.removeContentMutation,
            });
        },
        [
            container,
            containerCode,
            mutations.createContentMutation,
            mutations.removeContentMutation,
            mutations.updateContentMutation,
            readonly,
            windows,
        ],
    );

    const handleTakeForComplect = useCallback(
        async (closeActionSheet: () => void): Promise<void> => {
            if (container == null || !stockKanbanContainersServices.canTakeForComplect(container)) {
                return;
            }

            closeActionSheet();
            await mutations.takeForComplectMutation.mutateAsync({ containerCode: container.SoCode });
        },
        [container, mutations.takeForComplectMutation],
    );

    const handleCompleteContainer = useCallback(
        async (closeActionSheet: () => void): Promise<void> => {
            if (container == null || !stockKanbanContainersServices.canFinishKanbanComplect(container, currentUserCode)) {
                return;
            }

            closeActionSheet();

            if (!isFullyCollected) {
                const accepted = await confirm.show({
                    title: "Контейнер скомплектован не полностью",
                    message:
                        "Количество выбранных партий меньше нормативного количества контейнера. Завершить комплектование контейнера?",
                    confirmText: "Завершить",
                    cancelText: "Остаться",
                });

                if (!accepted) {
                    return;
                }
            }

            await mutations.completeContainerMutation.mutateAsync({ containerCode: container.SoCode });
        },
        [container, currentUserCode, isFullyCollected, mutations.completeContainerMutation],
    );

    const actionSheetSections = useMemo<ActionSheetSection[]>(
        () => [
            {
                id: "kanban-supply",
                items: [
                    {
                        type: "action",
                        id: "take-for-complect",
                        label: "Взять на комплектование",
                        description: canTake
                            ? "Назначит текущего пользователя комплектовщиком"
                            : "Доступно только для контейнера без комплектовщика",
                        disabled: !canTake,
                        loading: mutations.takeForComplectMutation.isPending,
                        shouldCloseOnSelect: false,
                        onSelect: (sheet) => {
                            void handleTakeForComplect(sheet.close);
                        },
                    },
                    {
                        type: "action",
                        id: "complete-container",
                        label: "Завершить комплектование контейнера",
                        description:
                            canFinish === false
                                ? "Доступно только комплектовщику контейнера на комплектовании"
                                : isFullyCollected
                                  ? "Контейнер укомплектован на нормативное количество"
                                  : "При неполной комплектации будет показано предупреждение",
                        disabled: canFinish === false,
                        loading: mutations.completeContainerMutation.isPending,
                        shouldCloseOnSelect: false,
                        onSelect: (sheet) => {
                            void handleCompleteContainer(sheet.close);
                        },
                    },
                ],
            },
        ],
        [
            canFinish,
            canTake,
            handleCompleteContainer,
            handleTakeForComplect,
            isFullyCollected,
            mutations.completeContainerMutation.isPending,
            mutations.takeForComplectMutation.isPending,
        ],
    );

    if (cardQuery.isFetching || container == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="stock_kanban_supply_card_page">
            <CardHeader
                text={`Контейнер №${container.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <DetailList className="stock_kanban_supply_card_inform" layout="tiles" columns="auto" minWidth={96} oneLine>
                <DetailList.Row label="Номенклатура" value={<BusinessStockObjValue stockObj={container.StockObj} />} />
                <DetailList.Row
                    label="Норм. кол-во"
                    value={<BusinessMeasureValue value={`${container.Qty}`} measure={container.Measure} />}
                />
                <DetailList.Row label="Статус комплект." value={formatComplectState(container.ComplectState)} />
                <DetailList.Row label="Дата взятия на комплект." value={formatEmptyDisplayValue(container.ComplectDate)} />
                <DetailList.Row
                    label="Потребитель"
                    value={<BusinessDivisionValue division={container.Division} section={container.Section} />}
                />
                <DetailList.Row label="Ячейки остатка" value={formatRemainderCells(container.RemainderCells)} />
                <DetailList.Row label="Комплектовщик" value={<BusinessUserValue user={container.ComplectUser} />} />
            </DetailList>
            <StockKanbanSupplyContentsList
                container={container}
                contentsQuery={contentsQuery}
                readonly={readonly}
                onCreateContent={() => {
                    const remainingQty = Math.max(container.Qty - collectedQty, 0);
                    openContentWindow(undefined, null, remainingQty);
                }}
                onOpenContent={(contentCode) => {
                    openContentWindow(contentCode);
                }}
            />
            <StockKanbanSupplyScanPlaceholder
                container={container}
                contents={contentsQuery.content}
                enabled={!readonly && !contentsQuery.isFetching && !isCardScannerSuppressedByWindow}
                onCreateContent={(lotCode, qty, spCode) => {
                    openContentWindow(undefined, lotCode, qty, spCode);
                }}
            />
        </div>
    );
};
